import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

// Facet dimensions exposed by the FilterBar. Each maps 1:1 to an API query
// param and a URL search param of the same name; values are comma-separated.
export const FACET_KINDS = ['tag', 'author', 'site', 'list', 'type'];

// The named view (favorites, history, unknown, metareview) is managed by the URL.
const VIEW_KIND = 'view';
const CHIP_KINDS = [VIEW_KIND, ...FACET_KINDS];

// The chips each entity grid honors. Every grid offers the named view; the
// facets are the item grid's alone.
export const GRID_CHIP_KINDS = {
  items: CHIP_KINDS,
  lists: [VIEW_KIND],
  authors: [VIEW_KIND],
  sites: [VIEW_KIND],
};

export const hasFilters = (params) => params.has('filter')
  || FACET_KINDS.some((kind) => params.has(kind));

export function stripFilters(params) {
  const next = new URLSearchParams(params);
  next.delete('filter');
  FACET_KINDS.forEach((kind) => next.delete(kind));
  next.delete('page');
  return next;
}

/**
 * The URL is the single source of truth for active facet filters. This hook
 * reads them from the query string and exposes mutators that rewrite it, so
 * filtered views are shareable, survive reload, and play with the back button.
 *
 * Returns:
 *  - facetParams: { tag: [...], author: [...], ... } (only non-empty) for fetches
 *  - activeFilters: flat [{ id, kind, value }] chips in stable kind order, the
 *    named view leading
 *  - addFilter / removeFilter / clearFilters: URL mutations (each resets paging)
 */
export default function useFacetFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const facetParams = useMemo(() => {
    const params = {};
    FACET_KINDS.forEach((kind) => {
      const raw = searchParams.get(kind);
      if (!raw) return;
      const values = raw.split(',').map((v) => v.trim()).filter(Boolean);
      if (values.length) params[kind] = values;
    });
    return params;
  }, [searchParams]);

  const view = searchParams.get(VIEW_KIND) || null;

  const activeFilters = useMemo(() => {
    const list = [];
    if (view) list.push({ id: `${VIEW_KIND}:${view}`, kind: VIEW_KIND, value: view });
    FACET_KINDS.forEach((kind) => {
      (facetParams[kind] || []).forEach((value) => {
        list.push({ id: `${kind}:${value}`, kind, value });
      });
    });
    return list;
  }, [facetParams, view]);

  // Changing any dimension changes the result set, so drop back to page 1.
  const setFacet = useCallback((kind, values) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (values.length) next.set(kind, values.join(','));
      else next.delete(kind);
      next.delete('page');
      return next;
    });
  }, [setSearchParams]);

  const addFilter = useCallback((kind, value) => {
    const v = String(value);
    const current = facetParams[kind] || [];
    if (current.includes(v)) return;
    setFacet(kind, [...current, v]);
  }, [facetParams, setFacet]);

  const removeFilter = useCallback((id) => {
    const sep = id.indexOf(':');
    const kind = id.slice(0, sep);
    const value = id.slice(sep + 1);
    if (kind === VIEW_KIND) {
      setFacet(kind, []);
      return;
    }
    const current = facetParams[kind] || [];
    setFacet(kind, current.filter((v) => v !== value));
  }, [facetParams, setFacet]);

  const clearFilters = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      CHIP_KINDS.forEach((kind) => next.delete(kind));
      next.delete('page');
      return next;
    });
  }, [setSearchParams]);

  return {
    facetParams, view, activeFilters, addFilter, removeFilter, clearFilters,
  };
}
